import { useEffect } from 'react';

import { useUser } from '@clerk/react';

import { useComposeSnapshotIndexStore } from '../store/ComposeSnapshotIndex';
import {
  createSyncRuntime,
  hasPendingSyncWork,
  resetSyncAttempts,
  runComposeSnapshotSyncPass,
} from '../utils/composeSnapshotSync';
import { DRAFT_AUTO_RETRY_DELAYS_MS } from '../utils/constants/drafts';
import { VISIBILITY_VISIBLE } from '../utils/constants/uiConstants';

const SYNC_EVENTS = {
  ONLINE: 'online',
  VISIBILITY_CHANGE: 'visibilitychange',
} as const;

export const useComposeSnapshotSync = (): void => {
  const { user } = useUser();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) {
      return undefined;
    }
    const runtime = createSyncRuntime();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let isRunning = false;
    let isRerunRequested = false;
    let isDisposed = false;
    let failureStreak = 0;

    const armBackoff = async () => {
      clearTimeout(timer);
      if (isDisposed || !(await hasPendingSyncWork(userId, runtime))) {
        return;
      }
      const delays = DRAFT_AUTO_RETRY_DELAYS_MS;
      const delay = delays[Math.min(failureStreak, delays.length - 1)];
      timer = setTimeout(() => void run(), delay);
    };

    const run = async (): Promise<void> => {
      if (isRunning) {
        isRerunRequested = true;
        return;
      }
      isRunning = true;
      try {
        do {
          isRerunRequested = false;
          const didFail = await runComposeSnapshotSyncPass(userId, runtime);
          failureStreak = didFail ? failureStreak + 1 : 0;
        } while (isRerunRequested && !isDisposed);
      } finally {
        isRunning = false;
      }
      await armBackoff();
    };

    const restart = () => {
      resetSyncAttempts(runtime);
      failureStreak = 0;
      void run();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === VISIBILITY_VISIBLE) {
        restart();
      }
    };

    const unsubscribe = useComposeSnapshotIndexStore.subscribe(
      (state, previous) => {
        if (
          state.entriesBySessionId !== previous.entriesBySessionId ||
          state.liveSessionIds !== previous.liveSessionIds
        ) {
          void run();
        }
      },
    );
    window.addEventListener(SYNC_EVENTS.ONLINE, restart);
    document.addEventListener(
      SYNC_EVENTS.VISIBILITY_CHANGE,
      handleVisibilityChange,
    );
    void run();

    return () => {
      isDisposed = true;
      clearTimeout(timer);
      unsubscribe();
      window.removeEventListener(SYNC_EVENTS.ONLINE, restart);
      document.removeEventListener(
        SYNC_EVENTS.VISIBILITY_CHANGE,
        handleVisibilityChange,
      );
    };
  }, [userId]);
};
